import { AnimationGroup, Matrix, Quaternion, TransformNode, Vector3 } from "@babylonjs/core";
import { GhostState } from "./GhostStateMachine";

export const PONTIANAK_REST_POSE_FRAME = 0;
export const PONTIANAK_AGGRESSION_BLEND_RESPONSE = 4.6;

const POSE_BONES = [
  "Spine",
  "LeftShoulder",
  "LeftArm",
  "LeftForeArm",
  "LeftHand",
  "RightShoulder",
  "RightArm",
  "RightForeArm",
  "RightHand",
  "Head",
] as const;

type PoseBoneName = typeof POSE_BONES[number];
type ArmSide="left"|"right";
type DirectedArmBone="LeftArm"|"LeftForeArm"|"RightArm"|"RightForeArm";

export interface PontianakReachDirections{readonly upperArm:Vector3;readonly forearm:Vector3;}

/** Character-local targets: +Z is the measured face direction and +Y is up. */
export function pontianakAggressiveReachDirections(side:ArmSide,timeSeconds:number,lungeAmount=0):PontianakReachDirections{
  const sideSign=side==="left"?-1:1;
  const reach=.5+.5*Math.sin(timeSeconds*(side==="left"?3.3:4.1)+(side==="left"?.4:2.1));
  const twitch=Math.sin(timeSeconds*7.9+(side==="left"?0:.8))*.025;
  const lunge=Math.max(0,Math.min(1,lungeAmount));
  const upperArm=new Vector3(sideSign*(.12+.03*reach),-.1+.03*reach+twitch,1+.18*lunge).normalize();
  const forearm=new Vector3(-sideSign*.025,.015-twitch*.4,1+.26*lunge).normalize();
  return{upperArm,forearm};
}

interface TransformSnapshot {
  readonly node: TransformNode;
  readonly position: Vector3;
  readonly scaling: Vector3;
  readonly rotation: Vector3;
  readonly rotationQuaternion: Quaternion | null;
}

export interface PontianakPoseDiagnostics {
  readonly activePose: string;
  readonly sourceClip: string;
  readonly sampledFrame: number;
  readonly playback: "sampled-frame-stopped" | "none";
  readonly skeletonBoneCount: number;
  readonly armsLowered: boolean;
  readonly armDrop: number;
}

export type PontianakActionPose = "none" | "mirror-reach" | "mirror-raise" | "mirror-hold" | "mirror-throw" | "hit" | "aggressive" | "lunge";

function nodeBySuffix(root: TransformNode, name: string): TransformNode | undefined {
  return root.getDescendants(false).find((node) => node instanceof TransformNode && (node.name === name || node.name.endsWith(`-${name}`))) as TransformNode | undefined;
}

function captureTransform(node: TransformNode): TransformSnapshot {
  return {
    node,
    position: node.position.clone(),
    scaling: node.scaling.clone(),
    rotation: node.rotation.clone(),
    rotationQuaternion: node.rotationQuaternion?.clone() ?? null,
  };
}

function restoreTransform(snapshot: TransformSnapshot): void {
  if (snapshot.node.isDisposed()) return;
  snapshot.node.position.copyFrom(snapshot.position);
  snapshot.node.scaling.copyFrom(snapshot.scaling);
  snapshot.node.rotation.copyFrom(snapshot.rotation);
  if (snapshot.rotationQuaternion) {
    if (!snapshot.node.rotationQuaternion) snapshot.node.rotationQuaternion = snapshot.rotationQuaternion.clone();
    else snapshot.node.rotationQuaternion.copyFrom(snapshot.rotationQuaternion);
  } else snapshot.node.rotationQuaternion = null;
}

function currentQuaternion(node: TransformNode): Quaternion {
  return node.rotationQuaternion?.clone() ?? Quaternion.FromEulerVector(node.rotation);
}

/**
 * Owns one Pontianak instance's stable skeletal pose. The shipped catwalk clip
 * is sampled once for its natural upper body; its Hips and leg channels are
 * never retained, so imported root motion cannot compete with navigation.
 */
export class PontianakPoseController {
  private readonly bones = new Map<PoseBoneName, TransformNode>();
  private readonly originalTransforms: TransformSnapshot[];
  private readonly basePose = new Map<PoseBoneName, Quaternion>();
  private readonly delta = Quaternion.Identity();
  private readonly composed = Quaternion.Identity();
  private readonly directedBoneAxes=new Map<DirectedArmBone,Vector3>();
  private readonly directedBoneChildren=new Map<DirectedArmBone,TransformNode>();
  private readonly alignment=Quaternion.Identity();
  private readonly directedTarget=Quaternion.Identity();
  private readonly localCandidate=Quaternion.Identity();
  private readonly bestLocalCandidate=Quaternion.Identity();
  private readonly desiredWorld=Vector3.Zero();
  private readonly desiredParent=Vector3.Zero();
  private readonly baseBoneDirection=Vector3.Zero();
  private readonly currentWorldDirection=Vector3.Zero();
  private readonly inverseParentMatrix=Matrix.Identity();
  private disposed = false;
  private sourceClip = "none";
  private playback: PontianakPoseDiagnostics["playback"] = "none";
  private activePose = "bind-pose";
  private actionPose:PontianakActionPose="none";
  private actionProgress=0;
  private aggressionBlend=0;
  private lastUpdateTime=0;

  constructor(private readonly model: TransformNode) {
    for (const name of POSE_BONES) {
      const node = nodeBySuffix(model, name);
      if (node) this.bones.set(name, node);
    }
    this.captureDirectedBoneAxis("LeftArm","LeftForeArm");
    this.captureDirectedBoneAxis("LeftForeArm","LeftHand");
    this.captureDirectedBoneAxis("RightArm","RightForeArm");
    this.captureDirectedBoneAxis("RightForeArm","RightHand");
    this.originalTransforms = [...this.bones.values()].map(captureTransform);
    this.sampleExistingPose();
    model.onDisposeObservable.addOnce(() => this.dispose());
  }

  update(timeSeconds: number, state: GhostState): void {
    if (this.disposed || this.basePose.size === 0) return;
    const interacting = state === GhostState.INTERACTING || state === GhostState.MIRROR_PICKUP || state === GhostState.MIRROR_THROWING;
    const capturing = state === GhostState.CAPTURING || state === GhostState.CAPTURED;
    const motionScale = capturing ? 0 : interacting ? 0.28 : 1;
    const slow = Math.sin(timeSeconds * 0.68);
    const slower = Math.sin(timeSeconds * 0.41 + 0.9);
    const deltaSeconds=Math.max(0,Math.min(.05,timeSeconds-this.lastUpdateTime));this.lastUpdateTime=timeSeconds;
    const aggressionTarget=state===GhostState.AGGRESSIVE?1:0;
    this.aggressionBlend+=(aggressionTarget-this.aggressionBlend)*(1-Math.exp(-deltaSeconds*PONTIANAK_AGGRESSION_BLEND_RESPONSE));
    if(this.actionPose==="none")this.activePose=this.aggressionBlend>.01?"angry-flail-blend-out":"mixamo-upper-body-rest";

    const smoothedActionProgress=this.actionProgress*this.actionProgress*(3-2*this.actionProgress);
    const action=this.actionOffsets(timeSeconds);
    this.applyRelative("Spine", slow * 0.012 * motionScale+action.spine[0], 0.018 + slower * 0.01 * motionScale+action.spine[1], slow * 0.012 * motionScale+action.spine[2]);
    this.applyRelative("Head", slower * 0.052 * motionScale+action.head[0], (interacting ? 0.035 : 0) + slow * 0.018 * motionScale+action.head[1], slow * 0.022 * motionScale+action.head[2]);
    this.applyRelative("LeftShoulder", action.leftShoulder[0],slower * 0.008 * motionScale+action.leftShoulder[1],slow * 0.012 * motionScale+action.leftShoulder[2]);
    this.applyRelative("RightShoulder", action.rightShoulder[0],-slower * 0.008 * motionScale+action.rightShoulder[1],-slow * 0.012 * motionScale+action.rightShoulder[2]);
    const aggressiveLayer=this.aggressionBlend>.0001&&(this.actionPose==="none"||this.actionPose==="aggressive"||this.actionPose==="lunge");
    if(aggressiveLayer)this.applyAggressiveArmDirections(timeSeconds,smoothedActionProgress);
    else{
      this.applyRelative("LeftArm",slow * 0.008 * motionScale+action.leftArm[0],action.leftArm[1],slower * 0.01 * motionScale+action.leftArm[2]);
      this.applyRelative("RightArm",-slow * 0.008 * motionScale+action.rightArm[0],action.rightArm[1],-slower * 0.01 * motionScale * (interacting ? 0.2 : 1)+action.rightArm[2]);
      this.applyRelative("LeftForeArm",action.leftForeArm[0],action.leftForeArm[1],slow * 0.006 * motionScale+action.leftForeArm[2]);
      this.applyRelative("RightForeArm",action.rightForeArm[0],action.rightForeArm[1],-slow * 0.006 * motionScale * (interacting ? 0.2 : 1)+action.rightForeArm[2]);
    }
    this.applyRelative("LeftHand",action.leftHand[0],action.leftHand[1],action.leftHand[2]);
    this.applyRelative("RightHand",action.rightHand[0],action.rightHand[1],action.rightHand[2]);
  }

  setActionPose(pose:PontianakActionPose,progress=0):void{this.actionPose=pose;this.actionProgress=Math.max(0,Math.min(1,progress));if(this.basePose.size>0)this.activePose=pose==="none"?"mixamo-upper-body-rest":pose;}

  get diagnostics(): PontianakPoseDiagnostics {
    const leftDrop = this.armDrop("LeftShoulder", "LeftHand");
    const rightDrop = this.armDrop("RightShoulder", "RightHand");
    const armDrop = Math.min(leftDrop, rightDrop);
    return {
      activePose: this.activePose,
      sourceClip: this.sourceClip,
      sampledFrame: PONTIANAK_REST_POSE_FRAME,
      playback: this.playback,
      skeletonBoneCount: Number(this.model.metadata?.skeletonBoneCount ?? 0),
      armsLowered: Number.isFinite(armDrop) && armDrop > 0.08,
      armDrop: Number.isFinite(armDrop) ? armDrop : 0,
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const snapshot of this.originalTransforms) restoreTransform(snapshot);
    this.basePose.clear();
    this.bones.clear();
    this.directedBoneAxes.clear();
    this.directedBoneChildren.clear();
  }

  private sampleExistingPose(): void {
    const groups = (this.model.metadata?.animationGroups as readonly AnimationGroup[] | undefined) ?? [];
    const group = groups.find((candidate) => /mixamo[._]?com$/i.test(candidate.name));
    if (!group) return;

    const animatedNodes = group.targetedAnimations
      .map((targeted) => targeted.target)
      .filter((target): target is TransformNode => target instanceof TransformNode);
    const snapshots = animatedNodes.map(captureTransform);
    group.start(false, 1, group.from, group.to);
    group.goToFrame(PONTIANAK_REST_POSE_FRAME);
    group.pause();
    for (const name of POSE_BONES) {
      const node = this.bones.get(name);
      if (node) this.basePose.set(name, currentQuaternion(node));
    }
    group.stop(true);
    for (const snapshot of snapshots) restoreTransform(snapshot);
    for (const name of POSE_BONES) this.applyRelative(name, 0, 0, 0);

    if (this.basePose.size > 0) {
      this.sourceClip = group.name;
      this.playback = "sampled-frame-stopped";
      this.activePose = "mixamo-upper-body-rest";
    }
  }

  private applyRelative(name: PoseBoneName, yaw: number, pitch: number, roll: number): void {
    const node = this.bones.get(name);
    const base = this.basePose.get(name);
    if (!node || !base || node.isDisposed()) return;
    Quaternion.RotationYawPitchRollToRef(yaw, pitch, roll, this.delta);
    base.multiplyToRef(this.delta, this.composed);
    if (!node.rotationQuaternion) node.rotationQuaternion = Quaternion.Identity();
    node.rotationQuaternion.copyFrom(this.composed);
    node.rotation.setAll(0);
  }

  private captureDirectedBoneAxis(name:DirectedArmBone,childName:PoseBoneName):void{
    const node=this.bones.get(name),child=this.bones.get(childName);if(!node||!child||child.parent!==node||child.position.lengthSquared()<.000001)return;
    this.directedBoneAxes.set(name,child.position.normalizeToNew());
    this.directedBoneChildren.set(name,child);
  }

  private applyAggressiveArmDirections(timeSeconds:number,smoothedProgress:number):void{
    const lunge=this.actionPose==="lunge"?Math.sin(Math.PI*smoothedProgress):0;
    const left=pontianakAggressiveReachDirections("left",timeSeconds,lunge),right=pontianakAggressiveReachDirections("right",timeSeconds,lunge);
    this.applyDirectionTarget("LeftArm",left.upperArm,this.aggressionBlend);
    this.applyDirectionTarget("RightArm",right.upperArm,this.aggressionBlend);
    this.applyDirectionTarget("LeftForeArm",left.forearm,this.aggressionBlend);
    this.applyDirectionTarget("RightForeArm",right.forearm,this.aggressionBlend);
  }

  private applyDirectionTarget(name:DirectedArmBone,characterLocalDirection:Vector3,blend:number):void{
    const node=this.bones.get(name),base=this.basePose.get(name),axis=this.directedBoneAxes.get(name),child=this.directedBoneChildren.get(name),parent=node?.parent;
    if(!node||!base||!axis||!child||!(parent instanceof TransformNode)||node.isDisposed()||child.isDisposed())return;
    if(!node.rotationQuaternion)node.rotationQuaternion=Quaternion.Identity();
    node.rotationQuaternion.copyFrom(base);node.rotation.setAll(0);
    this.model.computeWorldMatrix(true);parent.computeWorldMatrix(true);node.computeWorldMatrix(true);child.computeWorldMatrix(true);
    Vector3.TransformNormalToRef(characterLocalDirection,this.model.getWorldMatrix(),this.desiredWorld);this.desiredWorld.normalize();
    this.inverseParentMatrix.copyFrom(parent.getWorldMatrix()).invert();
    Vector3.TransformNormalToRef(this.desiredWorld,this.inverseParentMatrix,this.desiredParent);this.desiredParent.normalize();
    axis.rotateByQuaternionToRef(base,this.baseBoneDirection);this.baseBoneDirection.normalize();
    Quaternion.FromUnitVectorsToRef(this.baseBoneDirection,this.desiredParent,this.alignment);
    let bestScore=Number.NEGATIVE_INFINITY;
    const evaluate=(candidate:Quaternion):void=>{
      node.rotationQuaternion!.copyFrom(candidate);node.computeWorldMatrix(true);child.computeWorldMatrix(true);
      child.getAbsolutePosition().subtractToRef(node.getAbsolutePosition(),this.currentWorldDirection);this.currentWorldDirection.normalize();
      const score=Vector3.Dot(this.currentWorldDirection,this.desiredWorld);
      if(score>bestScore){bestScore=score;this.bestLocalCandidate.copyFrom(candidate);}
    };
    this.alignment.multiplyToRef(base,this.localCandidate);evaluate(this.localCandidate);
    base.multiplyToRef(this.alignment,this.localCandidate);evaluate(this.localCandidate);
    this.directedTarget.copyFrom(this.bestLocalCandidate);
    Quaternion.SlerpToRef(base,this.directedTarget,Math.max(0,Math.min(1,blend)),this.composed);
    node.rotationQuaternion.copyFrom(this.composed);node.rotation.setAll(0);node.computeWorldMatrix(true);
  }

  private actionOffsets(timeSeconds:number):{spine:[number,number,number];head:[number,number,number];leftShoulder:[number,number,number];rightShoulder:[number,number,number];leftArm:[number,number,number];rightArm:[number,number,number];leftForeArm:[number,number,number];rightForeArm:[number,number,number];leftHand:[number,number,number];rightHand:[number,number,number]}{
    const zero=():[number,number,number]=>[0,0,0];
    const result={spine:zero(),head:zero(),leftShoulder:zero(),rightShoulder:zero(),leftArm:zero(),rightArm:zero(),leftForeArm:zero(),rightForeArm:zero(),leftHand:zero(),rightHand:zero()};
    const t=this.actionProgress*this.actionProgress*(3-2*this.actionProgress);
    if(this.actionPose==="mirror-reach"){result.spine=[-.04*t,.03*t,.035*t];result.head=[.04*t,-.05*t,0];result.rightShoulder=[0,.08*t,-.12*t];result.rightArm=[-.12*t,.42*t,-.34*t];result.rightForeArm=[.08*t,-.58*t,.12*t];result.rightHand=[0,-.16*t,.08*t];}
    else if(this.actionPose==="mirror-raise"||this.actionPose==="mirror-hold"){
      const hold=this.actionPose==="mirror-hold"?1:t;result.spine=[0,.025*hold,.025*hold];result.head=[-.025*hold,.035*hold,-.018*hold];result.rightShoulder=[0,-.06*hold,-.18*hold];result.rightArm=[-.2*hold,-.48*hold,-.58*hold];result.rightForeArm=[.06*hold,-1.05*hold,.18*hold];result.rightHand=[0,.22*hold,-.08*hold];
    }else if(this.actionPose==="mirror-throw"){
      const pull=Math.min(1,t*2),follow=Math.max(0,(t-.45)/.55);result.spine=[-.08*pull+.2*follow,-.06*pull,.04*pull];result.head=[.12*follow,0,0];result.rightShoulder=[0,.12*pull,-.2*pull];result.rightArm=[-.22*pull+.55*follow,-.55*pull+.72*follow,-.65*pull+.4*follow];result.rightForeArm=[.08,-1.05*pull+1.38*follow,.18];result.rightHand=[0,.2*pull+.5*follow,-.1];
    }else if(this.actionPose==="hit"){result.spine=[.12*t,-.08*t,.1*t];result.head=[-.18*t,.06*t,-.08*t];}
    else if((this.actionPose==="none"||this.actionPose==="aggressive"||this.actionPose==="lunge")&&this.aggressionBlend>0){
      const blend=this.aggressionBlend,jerk=Math.sin(timeSeconds*7.9)+Math.sin(timeSeconds*13.7+.8)*.38;
      const leftReach=.5+.5*Math.sin(timeSeconds*3.3+.4),rightReach=.5+.5*Math.sin(timeSeconds*4.1+2.1);
      const lunge=this.actionPose==="lunge"?Math.sin(Math.PI*t):0;
      result.spine=[Math.sin(timeSeconds*2.7)*.1*blend,.13*blend+.24*lunge,(Math.sin(timeSeconds*3.5+.7)*.075+jerk*.018)*blend];
      result.head=[Math.sin(timeSeconds*8.3)*.105*blend,Math.sin(timeSeconds*5.7+1.4)*.065*blend,Math.sin(timeSeconds*4.9)*.06*blend];
      result.leftShoulder=[jerk*.012*blend,0,.018*blend];
      result.rightShoulder=[-jerk*.012*blend,0,-.018*blend];
      result.leftHand=[Math.sin(timeSeconds*10.1)*.13*blend,-.18*leftReach*blend,.12*blend];
      result.rightHand=[-Math.sin(timeSeconds*9.3+.5)*.13*blend,.18*rightReach*blend,-.12*blend];
    }
    return result;
  }

  private armDrop(shoulderName: "LeftShoulder" | "RightShoulder", handName: "LeftHand" | "RightHand"): number {
    const shoulder = this.bones.get(shoulderName) ?? nodeBySuffix(this.model, shoulderName);
    const hand = nodeBySuffix(this.model, handName);
    if (!shoulder || !hand || shoulder.isDisposed() || hand.isDisposed()) return Number.NaN;
    shoulder.computeWorldMatrix(true);
    hand.computeWorldMatrix(true);
    return shoulder.getAbsolutePosition().y - hand.getAbsolutePosition().y;
  }
}
